import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { mapJobAdDTO2Model } from '@/DTO/Assessments/JobAdDTO';
import { useAssessmentClient, useClientsClient, useJobAdClient, useSettingsClient } from '@/Hooks/ClientHooks';
import { Currency } from '@/Models/Currency';
import ProcessingState from '@/Models/ProcessingState';
import RoutePath from '@/Routing/RoutePath';
import { JobAd } from '../../Models/JobAd';
import { JobDetails } from '../../Models/JobDetails';
import {
    EditRecruitmentContextState,
    DefaultEditRecruitmentContextState,
    mapEditRecruitmentContextState2Dto,
} from './EditRecruitmentContextState';
import useTalentMeshHistory from '@/Routing/useTalentMeshHistory';
import { mapJobDetailsDTO2Model } from '../../Steps/JobDetailsStep/JobDetailsStepUtils';
import { ExperienceCriteria, mapExperienceCriteriaDTOToExperienceCriteria } from '@/Models/ExperienceCriteria';

interface IEditRecruitmentContext extends EditRecruitmentContextState {
    currencies: Currency[];
    gettingDataProcessingState: ProcessingState;

    setContextState: (value: EditRecruitmentContextState) => void;
    setProcessingState: (value: ProcessingState) => void;

    updateJobDetails: (newJobDetails: JobDetails) => Promise<void>;
    setJobAd: (newJobAd: JobAd) => void;
    updateRecruitmentWithoutExperienceCriteriaAsync: (jobAdValues: JobAd) => Promise<void>;
    updateRecruitmentWithExperienceCriteriaAsync: (
        experienceCriteriaValues: Array<ExperienceCriteria>,
    ) => Promise<void>;

    closeDialog: () => void;

    setExperienceCriteria: (valueSet: {
        experienceCriteria: EditRecruitmentContextState['experienceCriteria'];
    }) => void;
    resetExperienceCriteriaProcessingState: () => void;
    pollExperienceCriteriaScoreRecalculationStateAsync: () => Promise<void>;
}

const EditRecruitmentContext = createContext<IEditRecruitmentContext | undefined>(undefined);

const EditRecruitmentContextProvider = EditRecruitmentContext.Provider;

interface EditRecruitmentProviderProps {
    children: ReactNode;
}

export const EditRecruitmentProvider = ({ children }: EditRecruitmentProviderProps): React.JSX.Element => {
    const history = useTalentMeshHistory();
    const location = useLocation();
    const jobAdId = new URLSearchParams(location.search).get('jobAdId');
    if (jobAdId === null) {
        history.replace(RoutePath.Error);
    }

    const [state, setState] = useState<EditRecruitmentContextState>(DefaultEditRecruitmentContextState);
    const {
        recruitmentId,
        jobDetails,
        jobAd,
        client,
        processingState,
        regenerateAtStep2,
        showErrorDialog,
        includesExperienceCriteria,
        experienceCriteria,
        experienceCriteriaProcessingState,
        isExperienceScoreRecalculationComplete,
    } = state;

    const settingsClient = useSettingsClient();
    const jobAdClient = useJobAdClient();
    const clientsClient = useClientsClient();
    const assessmentClient = useAssessmentClient();

    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [gettingDataProcessingState, setGettingDataProcessingState] = useState(ProcessingState.Processing);

    const setProcessingState = (value: ProcessingState): void => {
        setState((prevState) => {
            return {
                ...prevState,
                processingState: value,
            };
        });
    };

    const setContextState = (value: EditRecruitmentContextState): void => {
        setState(value);
    };

    useEffect(() => {
        const loadDataAsync = async () => {
            try {
                const currenciesList = await settingsClient.getCurrenciesAsync();
                setCurrencies(currenciesList);

                const dto = await jobAdClient.getJobAdAsync(jobAdId ?? '');
                const retrievedClient = await clientsClient.getClientByIdAsync(dto.clientId);
                const experienceCriteriaDTOs = await assessmentClient.getExperienceCriteria(dto.recruitmentId);

                setContextState({
                    ...state,
                    recruitmentId: dto.recruitmentId,
                    jobDetails: mapJobDetailsDTO2Model(dto.jobDetails, currenciesList),
                    jobAd: { ...mapJobAdDTO2Model(dto) },
                    client: retrievedClient,
                    experienceCriteria: experienceCriteriaDTOs.map(mapExperienceCriteriaDTOToExperienceCriteria),
                    includesExperienceCriteria: experienceCriteriaDTOs.length > 0,
                });

                setGettingDataProcessingState(ProcessingState.Success);
            } catch (error) {
                setGettingDataProcessingState(ProcessingState.Error);
            }
        };
        loadDataAsync();
    }, []);

    const updateJobDetails = async (newJobDetails: JobDetails): Promise<void> => {
        setState((prevState) => {
            return {
                ...prevState,
                jobDetails: newJobDetails,
            };
        });
    };

    const setJobAd = (newJobAd: JobAd): void => {
        setState((prevState) => {
            return {
                ...prevState,
                jobAd: newJobAd,
            };
        });
    };

    const updateRecruitmentWithoutExperienceCriteriaAsync = async (jobAdValues: JobAd): Promise<void> => {
        setProcessingState(ProcessingState.Processing);
        try {
            const dto = mapEditRecruitmentContextState2Dto(jobDetails, jobAdValues, []);
            await assessmentClient.updateRecruitmentAsync(recruitmentId, dto);

            setProcessingState(ProcessingState.Success);
        } catch (e) {
            setProcessingState(ProcessingState.Error);
            throw e;
        }
    };

    const pollExperienceCriteriaScoreRecalculationStateAsync = async () => {
        const recalculationState = await assessmentClient.getExperienceScoreRecalculationStatusAsync(recruitmentId);

        // A verbose attempt to optimize state changes when polling on an interval
        if (isExperienceScoreRecalculationComplete !== recalculationState.isCompleted) {
            setState((prev) => ({
                ...prev,
                isExperienceScoreRecalculationComplete: recalculationState.isCompleted,
            }));
        }
    };

    const updateRecruitmentWithExperienceCriteriaAsync = async (
        experienceCriteriaValues: Array<ExperienceCriteria>,
    ): Promise<void> => {
        setProcessingState(ProcessingState.Processing);
        try {
            const dto = mapEditRecruitmentContextState2Dto(jobDetails, jobAd, experienceCriteriaValues);
            await assessmentClient.updateRecruitmentAsync(recruitmentId, dto);

            if (includesExperienceCriteria) {
                await pollExperienceCriteriaScoreRecalculationStateAsync();
            }

            setProcessingState(ProcessingState.Success);
        } catch (e) {
            setProcessingState(ProcessingState.Error);
            throw e;
        }
    };

    const closeDialog = () => {
        setState((prev) => {
            return {
                ...prev,
                showErrorDialog: undefined,
            };
        });
    };

    const resetExperienceCriteriaProcessingState = () => {
        setState((prev) => ({ ...prev, experienceCriteriaProcessingState: ProcessingState.Idle }));
    };

    const setExperienceCriteria: IEditRecruitmentContext['setExperienceCriteria'] = (newValue) => {
        setState((p) => ({
            ...p,
            experienceCriteria: newValue.experienceCriteria,
        }));
    };

    return (
        <EditRecruitmentContextProvider
            value={{
                recruitmentId,
                jobDetails,
                jobAd,
                client,
                processingState,
                regenerateAtStep2,
                currencies,
                gettingDataProcessingState,
                showErrorDialog,

                setContextState,
                setProcessingState,
                setJobAd,
                updateJobDetails,
                updateRecruitmentWithoutExperienceCriteriaAsync,
                updateRecruitmentWithExperienceCriteriaAsync,

                closeDialog,

                setExperienceCriteria,
                resetExperienceCriteriaProcessingState,

                includesExperienceCriteria,
                experienceCriteria,
                experienceCriteriaProcessingState,
                isExperienceScoreRecalculationComplete,
                pollExperienceCriteriaScoreRecalculationStateAsync,
            }}
        >
            {children}
        </EditRecruitmentContextProvider>
    );
};

export function useEditRecruitmentContext() {
    const context = useContext(EditRecruitmentContext);
    if (!context) {
        throw new Error('useEditRecruitmentContext must be used within the EditRecruitmentContext.Provider');
    }
    return context;
}
